import { Theme, styled } from "@mui/material/styles";
import { TypeOptions } from "react-toastify";

import { TOAST_MASCOT_SIZE } from "./data";
import { ToastTitles } from "./type";

const STILL = "@media (prefers-reduced-motion: reduce)";

const TONED: TypeOptions[] = ["default", "info", "success", "warning", "error"];

/** A plain `toast()` has no status of its own, so it wears the brand blue. */
const toneOf = (theme: Theme, type: TypeOptions) =>
  theme.vars.palette[type === "default" ? "primary" : type];

/** What each kind of toast does when it lands: a hop for good news, a head shake for bad. */
const GESTURE: Partial<Record<TypeOptions, string>> = {
  success: ", toast-mascot-hop 360ms 380ms ease-out",
  error: ", toast-mascot-shake 420ms 380ms ease-in-out",
};

/** The mascot's face in a disc, ringed in the toast's color. */
export const MascotRing = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: TypeOptions }>(({ theme, tone }) => {
  const color = toneOf(theme, tone);

  return {
    position: "relative",
    display: "block",
    width: TOAST_MASCOT_SIZE,
    height: TOAST_MASCOT_SIZE,
    flexShrink: 0,
    borderRadius: "50%",
    // Shows only until the image loads.
    backgroundColor: `rgba(${color.mainChannel} / 0.2)`,
    boxShadow: `0 0 0 2px ${color.main}, 0 0 0 5px rgba(${color.mainChannel} / 0.18)`,
    animation: `toast-mascot-pop 380ms cubic-bezier(0.34, 1.56, 0.64, 1) both${GESTURE[tone] ?? ""}`,
    "@keyframes toast-mascot-pop": {
      "0%": { opacity: 0, transform: "scale(0.4) rotate(-20deg)" },
      "100%": { opacity: 1, transform: "none" },
    },
    "@keyframes toast-mascot-hop": {
      "0%, 100%": { transform: "none" },
      "40%": { transform: "translateY(-4px) rotate(-4deg)" },
    },
    "@keyframes toast-mascot-shake": {
      "0%, 100%": { transform: "none" },
      "20%, 60%": { transform: "rotate(-9deg)" },
      "40%, 80%": { transform: "rotate(9deg)" },
    },
    [STILL]: { animation: "none" },
  };
});

export const MascotFace = styled("img")({
  display: "block",
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  objectFit: "cover",
});

/** The status, pinned to the disc so the color is not the only cue. */
export const StatusBadge = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: TypeOptions }>(({ theme, tone }) => {
  const color = toneOf(theme, tone);

  return {
    position: "absolute",
    right: -4,
    bottom: -4,
    width: 20,
    height: 20,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    fontSize: 10,
    color: color.contrastText,
    backgroundColor: color.main,
    border: `2px solid ${theme.vars.palette.background.paper}`,
    animation: "toast-badge-pop 260ms 260ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
    "@keyframes toast-badge-pop": {
      "0%": { transform: "scale(0)" },
      "100%": { transform: "scale(1)" },
    },
    [STILL]: { animation: "none" },
  };
});

/** Slides in from the side on a wide screen and drops from the header on a phone. */
const PHONE = "@media (max-width: 480px)";

/**
 * The toast card. Scoped under `.Toastify` so it outranks the library's own
 * sheet whichever is injected last. `titles` is the heading per type, in the
 * player's language.
 */
export const toastGlobalStyles = (theme: Theme, titles: ToastTitles) => ({
  ".Toastify": {
    "--toastify-toast-width": "360px",
    "--toastify-font-family": theme.typography.fontFamily,
    // The library resolves its progress colors on :root, so they are set here directly.
    "--toastify-color-progress-light": theme.vars.palette.primary.main,
    "--toastify-color-progress-dark": theme.vars.palette.primary.main,
    "--toastify-color-progress-success": theme.vars.palette.success.main,
    "--toastify-color-progress-error": theme.vars.palette.error.main,
    "--toastify-color-progress-warning": theme.vars.palette.warning.main,
    "--toastify-color-progress-info": theme.vars.palette.info.main,
  },
  ".Toastify .Toastify__toast": {
    // Mascot in the first column across both rows; heading over the message in the second.
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    alignItems: "center",
    alignContent: "center",
    columnGap: 12,
    rowGap: 1,
    minHeight: 68,
    padding: "12px 42px 12px 14px",
    marginBottom: 10,
    overflow: "hidden",
    borderRadius: 14,
    border: "1px solid",
    color: theme.vars.palette.text.primary,
    backgroundColor: theme.vars.palette.background.paper,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
    boxShadow: `0 12px 32px rgba(${theme.vars.palette.primary.mainChannel} / 0.18)`,
    ...theme.applyStyles("dark", { boxShadow: "0 12px 32px rgba(0 0 0 / 0.5)" }),
  },
  ...Object.fromEntries(
    TONED.map((type) => {
      const color = toneOf(theme, type);

      const title = titles[type];

      return [
        `.Toastify .Toastify__toast--${type}`,
        {
          borderColor: `rgba(${color.mainChannel} / 0.35)`,
          // The color comes in from the mascot's side and fades before the text ends.
          backgroundImage: `linear-gradient(100deg, rgba(${color.mainChannel} / 0.16), transparent 62%)`,
          // The mascot's line. Generated content, so no call site has to pass a title.
          ...(title && {
            "&::before": {
              content: JSON.stringify(title),
              gridColumn: 2,
              gridRow: 1,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.02em",
              color: color.dark,
              ...theme.applyStyles("dark", { color: color.main }),
            },
          }),
        },
      ];
    }),
  ),
  ".Toastify .Toastify__toast-icon": {
    gridColumn: 1,
    gridRow: "1 / span 2",
    width: "auto",
    margin: 0,
    // The mascot animates itself; the library's zoom on top of it doubles the motion.
    animation: "none",
  },
  // The glint off his sunglasses, once across a win.
  ".Toastify .Toastify__toast--success::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "linear-gradient(105deg, transparent 35%, rgba(255 255 255 / 0.22) 50%, transparent 65%)",
    animation: "toast-glint 900ms 450ms ease-out both",
    [STILL]: { display: "none" },
  },
  "@keyframes toast-glint": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },
  ".Toastify .Toastify__close-button": {
    top: 6,
    right: 6,
    width: 28,
    height: 28,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    color: theme.vars.palette.text.secondary,
    opacity: 0.7,
    transition: "opacity 150ms, background-color 150ms",
    "& svg": { width: 12, height: 14 },
    "&:hover, &:focus-visible": { opacity: 1, backgroundColor: theme.vars.palette.action.hover },
  },
  ".Toastify .toast-enter": { animation: "toast-in 420ms cubic-bezier(0.21, 1.02, 0.73, 1) both" },
  ".Toastify .toast-exit": { animation: "toast-out 220ms ease-in both" },
  "@keyframes toast-in": {
    "0%": { opacity: 0, transform: "translateX(48px) scale(0.96)" },
    "100%": { opacity: 1, transform: "none" },
  },
  "@keyframes toast-out": {
    "0%": { opacity: 1, transform: "none" },
    "100%": { opacity: 0, transform: "translateX(48px) scale(0.96)" },
  },
  "@keyframes toast-drop-in": {
    "0%": { opacity: 0, transform: "translateY(-16px) scale(0.96)" },
    "100%": { opacity: 1, transform: "none" },
  },
  "@keyframes toast-lift-out": {
    "0%": { opacity: 1, transform: "none" },
    "100%": { opacity: 0, transform: "translateY(-12px) scale(0.96)" },
  },
  "@keyframes toast-fade": { "0%": { opacity: 0 }, "100%": { opacity: 1 } },
  ".Toastify .Toastify__progress-bar--wrp": { height: 3 },
  [PHONE]: {
    // The library runs a phone toast edge to edge with square corners; keep the card.
    ".Toastify .Toastify__toast-container": { padding: "0 12px", boxSizing: "border-box" },
    ".Toastify .toast-enter": { animationName: "toast-drop-in" },
    ".Toastify .toast-exit": { animationName: "toast-lift-out" },
  },
  // Still a fade: the library waits for the animation to end before it removes the toast.
  [STILL]: {
    ".Toastify .toast-enter": { animationName: "toast-fade" },
    ".Toastify .toast-exit": { animationName: "toast-fade", animationDirection: "reverse" },
  },
});
